/*
  component-header-editorial.js — Glossee desktop header, Phase 2 / 2.3 / 2.7 / 3.4
  Desktop-only (>= 1200px since Phase 3.4.1, reference boundary) behaviour for the editorial header:
  - ONE shared mega menu shell (fixed height, CSS) for the top menu panels and the desktop hamburger panel
  - top menu items: hover intent or keyboard focus opens their rich panel; moving to another item swaps the
    content inside the open shell (cross-fade) while shell, page overlay and white header stay unchanged
  - desktop hamburger: opens and closes on click / Enter / Space only (never on hover or focus), X while open;
    rows on the left switch the middle / right content on hover; default cards while the row has none
  - media cards: a video plays only while its panel is visible (never under reduced motion), otherwise paused
  - resize: once resizing settles the videos are checked again, so a card hidden by a breakpoint never keeps playing
  - header state hooks: section.is-hover / is-mega-open + body.glossee-mega-open (page overlay)
  Phase 3.4 (measured on us.doonails.com): top items open after 100ms hover intent; the category rail inside a
    panel switches the middle links on mouseenter / focus with no delay and keeps the last hovered item; the
    entering panel's columns are staggered (opacity + translateY: 250ms / 100+60i on open, 220ms / 40i on switch);
    the blocks-based mobile drawer rows open in place (500ms height animation) — no push panels.
  Reuses: the theme's HeaderWrapper hover state (is-active), transparent header CSS, sticky header.
  Does not touch: search-modal, cart-drawer, mini-cart, shopify-account, MenuDrawer/HeaderDrawer.
*/
(function () {
  'use strict';
  if (customElements.get('glossee-mega-menu')) return;

  var DESKTOP = '(min-width: 1200px)'; // Phase 3.4.1: reference desktop boundary (was 990px)
  var OPEN_DELAY = 100;     // hover intent before a closed shell opens for a top menu item (reference: 100ms)
  var SWITCH_DELAY = 90;    // hover intent before the hamburger panel hands the shell over to a top menu item
  var ROW_DELAY = 60;       // hover intent between hamburger rows
  var CLOSE_DELAY = 120;    // grace when the pointer leaves header + shell (reference closes at once, fades 250ms)
  var FADE_MS = 180;        // panel cross-fade, matches the CSS transition
  var SHELL_MS = 250;       // shell fade (reference: opacity .25s ease-in-out)
  var STAGGER_EASE = 'cubic-bezier(0.65, 0, 0.35, 1)';
  var MOBILE_ACCORDION_MS = 500;
  var SCROLL_CLOSE_PX = 8;  // close after this much scroll
  var RESIZE_SYNC_MS = 120; // debounce before the videos are checked again after a resize

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function focusables(root) {
    if (!root) return [];
    return Array.prototype.filter.call(
      root.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([type="hidden"]), select, textarea'),
      function (el) { return el.offsetParent !== null || el === document.activeElement; }
    );
  }

  function isEscape(event) { return event.key === 'Escape' || event.key === 'Esc'; }

  class GlosseeMegaMenu extends HTMLElement {
    connectedCallback() {
      this.mq = window.matchMedia(DESKTOP);
      this.section = this.closest('.shopify-section-header');
      this.wrapper = this.closest('.header-wrapper');
      this.header = this.wrapper ? this.wrapper.querySelector('.header') : null;
      if (!this.section || !this.wrapper || !this.header) return;

      this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-glossee-mega-panel]'));
      this.triggers = Array.prototype.slice.call(this.header.querySelectorAll('[data-glossee-mega-trigger]'));
      this.aside = this.querySelector('[data-glossee-hamburger]');
      this.asidePromos = this.querySelector('[data-glossee-hamburger-promos]');
      this.rows = Array.prototype.slice.call(this.querySelectorAll('[data-glossee-hamburger-row]'));
      this.linkRows = Array.prototype.slice.call(this.querySelectorAll('[data-glossee-hamburger-link]'));
      this.rowNav = this.aside ? Array.prototype.slice.call(this.aside.querySelectorAll('a.glossee-mega__row, button.glossee-mega__row')) : [];
      this.cats = Array.prototype.slice.call(this.querySelectorAll('[data-glossee-cat]'));
      this.hamburgerDetails = this.header.querySelector('.header__left header-drawer > details');
      this.hamburgerSummary = this.hamburgerDetails ? this.hamburgerDetails.querySelector('summary') : null;
      this.hamburgerEnabled = !!(this.aside && this.hamburgerSummary && this.header.classList.contains('header--desktop-hamburger'));

      this.openId = null;      // panel shown in the shell (top menu item or hamburger row), or null
      this.mode = null;        // 'panel' | 'hamburger' | null
      this.lastTrigger = null; // element to return focus to
      this.openTimer = null;
      this.closeTimer = null;
      this.rowTimer = null;
      this.cleanupTimer = null;
      this.videoSyncTimer = null;
      this.lastScrollY = window.pageYOffset;
      this.imagesWarmed = false;

      this.syncHamburgerAttributes();
      this.bind();
    }

    disconnectedCallback() {
      if (this.unbind) this.unbind();
      this.cancelVideoSync();
      this.pauseAllVideos();
    }

    /* ------------------------------------------------------------------ events */
    bind() {
      var self = this;
      var handlers = [];
      function on(target, type, fn, opts) {
        target.addEventListener(type, fn, opts);
        handlers.push(function () { target.removeEventListener(type, fn, opts); });
      }

      // top menu items: hover intent / focus open the item's rich panel
      this.triggers.forEach(function (trigger) {
        var id = trigger.getAttribute('data-glossee-mega-trigger');
        on(trigger, 'mouseenter', function () { self.scheduleOpen(id, trigger); });
        on(trigger, 'mouseleave', function () { self.cancelOpen(); });
        on(trigger, 'focusin', function () {
          if (self.restoringFocus) return;
          self.warmImages();
          self.open(id, trigger, { focusPanel: false });
        });
        on(trigger, 'click', function (event) {
          if (!self.isDesktop() || trigger.tagName !== 'BUTTON') return;
          event.preventDefault();
          if (self.openId === id && self.mode === 'panel') self.close({ restoreFocus: false });
          else self.open(id, trigger, { focusPanel: false });
          // <a> triggers keep navigating; hover/focus already opened the panel
        });
        on(trigger, 'keydown', function (event) {
          if (!self.isDesktop()) return;
          if (event.key === 'ArrowDown' || event.key === 'Down') {
            event.preventDefault();
            self.open(id, trigger, { focusPanel: true });
          } else if (isEscape(event) && self.mode) {
            event.preventDefault();
            self.close({ restoreFocus: true });
          }
        });
      });

      // pointer safety: moving between header and shell never closes; leaving both closes a top menu panel.
      // The click-opened hamburger panel stays open until click / Escape / outside click / scroll.
      on(this.wrapper, 'mouseenter', function () { self.cancelClose(); self.warmImages(); });
      on(this.wrapper, 'mouseleave', function () {
        self.cancelOpen();
        self.cancelRow();
        if (self.mode === 'panel') self.scheduleClose();
      });

      // hamburger rows: rows with content switch the middle / right content, plain link rows show the default
      this.rows.forEach(function (row) {
        var id = row.getAttribute('data-glossee-hamburger-row');
        on(row, 'mouseenter', function () { self.scheduleRow(id); });
        on(row, 'mouseleave', function () { self.cancelRow(); });
        on(row, 'focus', function () { self.activateRow(id); });
        on(row, 'click', function (event) {
          if (row.tagName !== 'BUTTON') return; // link rows navigate
          event.preventDefault();
          self.activateRow(id, { focusPanel: true });
        });
      });
      this.linkRows.forEach(function (row) {
        on(row, 'mouseenter', function () { self.scheduleRow(null); });
        on(row, 'mouseleave', function () { self.cancelRow(); });
        on(row, 'focus', function () { self.activateRow(null); });
      });
      this.rowNav.forEach(function (row) {
        on(row, 'keydown', function (event) { self.onRowKeydown(event, row); });
      });

      // category rail (layout "cats"): hover or focus switches the middle links at once; the choice sticks
      this.cats.forEach(function (cat) {
        on(cat, 'mouseenter', function () { self.activateCat(cat); });
        on(cat, 'focus', function () { self.activateCat(cat); });
        on(cat, 'click', function (event) {
          if (cat.tagName !== 'BUTTON') return; // link rows navigate
          event.preventDefault();
          self.activateCat(cat, { focusLinks: true });
        });
        on(cat, 'keydown', function (event) { self.onCatKeydown(event, cat); });
      });

      // desktop hamburger: click only. Enter / Space on the <summary> fire a click, so this capture listener
      // covers mouse and keyboard; it runs before the theme's HeaderDrawer handler. Hover / focus never open.
      if (this.hamburgerEnabled) {
        on(document, 'click', function (event) {
          if (!self.isDesktop()) return;
          var summary = event.target.closest ? event.target.closest('summary') : null;
          if (!summary || summary !== self.hamburgerSummary) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          if (self.mode === 'hamburger') self.close({ restoreFocus: false });
          else self.openHamburger({ focusPanel: false });
        }, true);
        on(this.hamburgerSummary, 'keydown', function (event) {
          if (!self.isDesktop()) return;
          if (isEscape(event)) {
            if (self.mode === 'hamburger') { event.preventDefault(); self.close({ restoreFocus: true }); }
          } else if (event.key === 'ArrowDown' || event.key === 'Down') {
            event.preventDefault();
            self.openHamburger({ focusPanel: true });
          } else if (event.key === 'Tab' && !event.shiftKey && self.mode === 'hamburger') {
            // the next elements in DOM order are the top menu items; go into the open panel instead
            var first = focusables(self.aside)[0];
            if (first) { event.preventDefault(); first.focus(); }
          }
        });
        on(this.aside, 'keydown', function (event) {
          if (event.key !== 'Tab' || !event.shiftKey || self.mode !== 'hamburger') return;
          var items = focusables(self.aside);
          if (items.length && document.activeElement === items[0]) {
            event.preventDefault();
            self.hamburgerSummary.focus();
          }
        });
      }

      // global: Escape, outside click, focus leaving the header, scroll, resize
      on(document, 'keydown', function (event) {
        if (isEscape(event) && self.mode) self.close({ restoreFocus: true });
      });
      on(document, 'click', function (event) {
        if (!self.mode) return;
        if (self.section.contains(event.target)) return;
        self.close({ restoreFocus: false });
      }, true);
      on(document, 'focusin', function (event) {
        if (!self.mode) return;
        if (self.withinMenuScope(event.target)) return;
        self.close({ restoreFocus: false });
      });
      on(window, 'scroll', function () {
        var y = window.pageYOffset;
        if (self.mode && Math.abs(y - self.lastScrollY) > SCROLL_CLOSE_PX) self.close({ restoreFocus: false });
        self.lastScrollY = y;
      }, { passive: true });
      on(window, 'resize', function () {
        if (self.mode) self.updateOffset();
        // a breakpoint can hide or reveal a media card while the shell is open
        self.scheduleVideoSync();
      }, { passive: true });
      var onMq = function () { if (!self.isDesktop()) self.close({ restoreFocus: false }); self.syncHamburgerAttributes(); };
      if (this.mq.addEventListener) on(this.mq, 'change', onMq);

      // search / cart triggers in the header close the menu first
      Array.prototype.forEach.call(this.header.querySelectorAll('.header__right summary'), function (summary) {
        on(summary, 'click', function () { self.close({ restoreFocus: false }); }, true);
      });

      // Theme Editor: open the panel that belongs to the selected block
      if (window.Shopify && window.Shopify.designMode) {
        on(document, 'shopify:block:select', function (event) {
          var target = event.target;
          if (!target || !self.section.contains(target)) return;
          var trigger = target.matches && target.matches('li') ? target.querySelector('[data-glossee-mega-trigger]') : null;
          if (trigger) { self.open(trigger.getAttribute('data-glossee-mega-trigger'), trigger, { focusPanel: false }); return; }
          var row = self.aside && self.aside.contains(target) && target.querySelector ? target.querySelector('[data-glossee-hamburger-row]') : null;
          if (row) {
            self.openHamburger({ focusPanel: false });
            self.activateRow(row.getAttribute('data-glossee-hamburger-row'));
            return;
          }
          var panel = target.closest ? target.closest('[data-glossee-mega-panel]') : null;
          if (panel) {
            var id = panel.getAttribute('data-glossee-mega-panel');
            if (panel.getAttribute('data-glossee-panel-scope') === 'hamburger') {
              self.openHamburger({ focusPanel: false });
              self.activateRow(id);
            } else {
              self.open(id, self.triggerFor(id), { focusPanel: false });
            }
            var cat = target.matches && target.matches('[data-glossee-cat]') ? target : (target.closest ? target.closest('[data-glossee-cat]') : null);
            if (cat) self.activateCat(cat);
            return;
          }
          if (self.aside && (self.aside.contains(target) || (self.asidePromos && self.asidePromos.contains(target)))) {
            self.openHamburger({ focusPanel: false });
          }
        });
        on(document, 'shopify:block:deselect', function (event) {
          if (event.target && self.section.contains(event.target)) self.close({ restoreFocus: false });
        });
      }

      this.unbind = function () { handlers.forEach(function (off) { off(); }); };
    }

    /* ------------------------------------------------------------------ helpers */
    isDesktop() { return this.mq.matches; }

    /* on desktop the hamburger controls the shell; below 990px the theme's drawer owns it */
    syncHamburgerAttributes() {
      if (!this.hamburgerSummary || !this.hamburgerEnabled) return;
      if (this.isDesktop()) {
        if (this.id) this.hamburgerSummary.setAttribute('aria-controls', this.id);
        if (!this.hamburgerSummary.hasAttribute('aria-expanded')) this.hamburgerSummary.setAttribute('aria-expanded', 'false');
      } else if (this.hamburgerSummary.getAttribute('aria-controls') === this.id) {
        this.hamburgerSummary.removeAttribute('aria-controls');
      }
    }

    /* focus may move between the top menu items, the hamburger and the shell without closing */
    withinMenuScope(el) {
      if (!el || !el.closest) return false;
      if (this.contains(el)) return true;
      if (el === this.hamburgerSummary || (this.hamburgerSummary && this.hamburgerSummary.contains(el))) return true;
      var nav = this.header.querySelector('.header__inline-menu--blocks');
      return !!(nav && nav.contains(el));
    }

    triggerFor(id) {
      for (var i = 0; i < this.triggers.length; i++) {
        if (this.triggers[i].getAttribute('data-glossee-mega-trigger') === id) return this.triggers[i];
      }
      return null;
    }

    panelFor(id) {
      for (var i = 0; i < this.panels.length; i++) {
        if (this.panels[i].getAttribute('data-glossee-mega-panel') === id) return this.panels[i];
      }
      return null;
    }

    warmImages() {
      if (this.imagesWarmed || !this.isDesktop()) return;
      this.imagesWarmed = true;
      Array.prototype.forEach.call(this.querySelectorAll('img[loading="lazy"]'), function (img) {
        img.setAttribute('loading', 'eager');
      });
    }

    /* the shell never reaches below the screen: CSS caps its height by the space under the header */
    updateOffset() {
      var bottom = this.wrapper.getBoundingClientRect().bottom;
      this.style.setProperty('--glossee-mega-offset', Math.max(0, Math.round(bottom)) + 'px');
    }

    /* ------------------------------------------------------------------ timers */
    scheduleOpen(id, trigger) {
      if (!this.isDesktop()) return;
      this.cancelClose();
      this.cancelOpen();
      this.warmImages();
      if (this.mode === 'panel') {
        // already showing a top menu panel: swap the content inside the open shell
        if (this.openId !== id) this.open(id, trigger, { focusPanel: false });
        return;
      }
      var self = this;
      this.openTimer = setTimeout(function () {
        self.openTimer = null;
        self.open(id, trigger, { focusPanel: false });
      }, this.mode === 'hamburger' ? SWITCH_DELAY : OPEN_DELAY);
    }

    cancelOpen() { if (this.openTimer) { clearTimeout(this.openTimer); this.openTimer = null; } }

    scheduleClose() {
      var self = this;
      this.cancelClose();
      this.closeTimer = setTimeout(function () {
        self.closeTimer = null;
        self.close({ restoreFocus: false });
      }, CLOSE_DELAY);
    }

    cancelClose() { if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = null; } }

    scheduleRow(id) {
      if (this.mode !== 'hamburger') return;
      this.cancelRow();
      if (this.openId === id) return;
      var self = this;
      this.rowTimer = setTimeout(function () {
        self.rowTimer = null;
        self.activateRow(id);
      }, ROW_DELAY);
    }

    cancelRow() { if (this.rowTimer) { clearTimeout(this.rowTimer); this.rowTimer = null; } }

    cancelCleanup() { if (this.cleanupTimer) { clearTimeout(this.cleanupTimer); this.cleanupTimer = null; } }

    /* resize: one syncVideos() after resizing settles (plays only visible videos of the open panel, pauses the rest) */
    scheduleVideoSync() {
      var self = this;
      this.cancelVideoSync();
      this.videoSyncTimer = setTimeout(function () {
        self.videoSyncTimer = null;
        self.syncVideos();
      }, RESIZE_SYNC_MS);
    }

    cancelVideoSync() { if (this.videoSyncTimer) { clearTimeout(this.videoSyncTimer); this.videoSyncTimer = null; } }

    /* ------------------------------------------------------------------ state */
    setShellState(open) {
      if (open) this.updateOffset();
      this.classList.toggle('is-open', open);
      this.section.classList.toggle('is-hover', open);      // theme transparent-header hook: white shell
      this.section.classList.toggle('is-mega-open', open);
      document.body.classList.toggle('glossee-mega-open', open);
      this.setAttribute('aria-hidden', open ? 'false' : 'true');
    }

    /* show one panel (or none): the previous one fades out in the same grid cell, nothing is resized */
    showPanel(id) {
      var self = this;
      var wasOpen = this.classList.contains('is-open');
      var fade = wasOpen && !reducedMotion();
      this.panels.forEach(function (panel) {
        var active = id !== null && panel.getAttribute('data-glossee-mega-panel') === id;
        if (panel.glosseeHideTimer) { clearTimeout(panel.glosseeHideTimer); panel.glosseeHideTimer = null; }
        if (active) {
          var entering = panel.hidden || !panel.classList.contains('is-active');
          if (panel.hidden) {
            panel.classList.remove('is-active');
            panel.hidden = false;
            void panel.offsetWidth; // start the fade from the transparent state
          }
          panel.classList.add('is-active');
          if (entering) self.staggerColumns(panel, wasOpen);
        } else if (!panel.hidden) {
          panel.classList.remove('is-active');
          if (fade) {
            panel.glosseeHideTimer = setTimeout(function () {
              panel.glosseeHideTimer = null;
              if (!panel.classList.contains('is-active')) panel.hidden = true;
            }, FADE_MS + 20);
          } else {
            panel.hidden = true;
          }
        }
      });
      this.triggers.forEach(function (trigger) {
        var on = self.mode === 'panel' && trigger.getAttribute('data-glossee-mega-trigger') === id;
        trigger.setAttribute('aria-expanded', on ? 'true' : 'false');
      });
      this.rows.forEach(function (row) {
        var on = self.mode === 'hamburger' && row.getAttribute('data-glossee-hamburger-row') === id;
        row.setAttribute('aria-expanded', on ? 'true' : 'false');
        row.classList.toggle('is-active', on);
      });
      this.updateFallbackPromos();
      this.syncVideos();
    }

    /* reference motion: the entering panel's columns rise in one after the other
       (open: 20px / 250ms / 100 + 60i ms; switch between panels: 10px / 220ms / 40i ms) */
    staggerColumns(panel, isSwitch) {
      if (reducedMotion() || typeof panel.animate !== 'function') return;
      var cols = panel.querySelectorAll('.glossee-mega__col');
      Array.prototype.forEach.call(cols, function (col, i) {
        col.getAnimations().forEach(function (a) { if (a.id === 'glossee-stagger') a.cancel(); });
        var a = col.animate(
          [{ opacity: 0, transform: isSwitch ? 'translateY(10px)' : 'translateY(20px)' }, { opacity: 1, transform: 'none' }],
          { duration: isSwitch ? 220 : 250, delay: isSwitch ? 40 * i : 100 + 60 * i, easing: STAGGER_EASE, fill: 'backwards' }
        );
        a.id = 'glossee-stagger';
      });
    }

    /* category rail: show the links of one rail item inside its panel (no delay, sticky choice) */
    activateCat(cat, options) {
      var panel = cat.closest('[data-glossee-mega-panel]');
      if (!panel) return;
      var id = cat.getAttribute('data-glossee-cat');
      Array.prototype.forEach.call(panel.querySelectorAll('[data-glossee-cat]'), function (el) {
        var on = el === cat;
        el.classList.toggle('is-active', on);
        el.setAttribute('aria-expanded', on ? 'true' : 'false');
      });
      var subs = panel.querySelectorAll('[data-glossee-sub]');
      var target = null;
      Array.prototype.forEach.call(subs, function (sub) {
        var on = sub.getAttribute('data-glossee-sub') === id;
        sub.hidden = !on;
        if (on) target = sub;
      });
      if (target && options && options.focusLinks) {
        var first = focusables(target)[0];
        if (first) first.focus();
      }
    }

    onCatKeydown(event, cat) {
      var panel = cat.closest('[data-glossee-mega-panel]');
      if (!panel) return;
      var cats = Array.prototype.slice.call(panel.querySelectorAll('[data-glossee-cat]'));
      var idx = cats.indexOf(cat);
      var next = null;
      if (event.key === 'ArrowDown' || event.key === 'Down') {
        next = cats[Math.min(idx + 1, cats.length - 1)];
      } else if (event.key === 'ArrowUp' || event.key === 'Up') {
        next = cats[Math.max(idx - 1, 0)];
      } else if (event.key === 'ArrowRight' || event.key === 'Right') {
        event.preventDefault();
        this.activateCat(cat, { focusLinks: true });
        return;
      }
      if (next) { event.preventDefault(); next.focus(); }
    }

    /* hamburger: the default cards are shown whenever the active row has no cards of its own */
    updateFallbackPromos() {
      var show = false;
      if (this.asidePromos && this.mode === 'hamburger') {
        var panel = this.openId ? this.panelFor(this.openId) : null;
        show = !(panel && panel.hasAttribute('data-glossee-has-promos'));
      }
      if (this.asidePromos) this.asidePromos.hidden = !show;
      this.classList.toggle('glossee-mega--fallback-promos', show);
    }

    /* top menu item panel (hamburger row panels are only shown through activateRow) */
    open(id, trigger, options) {
      if (!this.isDesktop()) return;
      var panel = this.panelFor(id);
      if (!panel || panel.getAttribute('data-glossee-panel-scope') === 'hamburger') return;
      this.cancelOpen();
      this.cancelClose();
      this.cancelRow();
      this.cancelCleanup();
      this.mode = 'panel';
      this.openId = id;
      if (trigger) this.lastTrigger = trigger;
      this.classList.remove('glossee-mega--hamburger');
      if (this.aside) this.aside.hidden = true;
      this.setHamburgerState(false);
      this.setShellState(true);
      this.showPanel(id);
      if (options && options.focusPanel) {
        var first = focusables(panel)[0];
        if (first) first.focus();
      }
    }

    openHamburger(options) {
      if (!this.isDesktop() || !this.hamburgerEnabled) return;
      this.cancelOpen();
      this.cancelClose();
      this.cancelRow();
      this.cancelCleanup();
      this.mode = 'hamburger';
      this.openId = null;
      this.lastTrigger = this.hamburgerSummary;
      this.classList.add('glossee-mega--hamburger');
      this.aside.hidden = false;
      this.setHamburgerState(true);
      this.setShellState(true);
      this.showPanel(null);
      if (options && options.focusPanel) {
        var first = focusables(this.aside)[0];
        if (first) first.focus();
      }
    }

    /* hamburger rows: id = row with content, null = default state (no row active) */
    activateRow(id, options) {
      if (this.mode !== 'hamburger') return;
      this.cancelRow();
      if (id !== null && !this.panelFor(id)) return;
      if (this.openId !== id) {
        this.openId = id;
        this.showPanel(id);
      }
      if (id !== null && options && options.focusPanel) {
        var first = focusables(this.panelFor(id))[0];
        if (first) first.focus();
      }
    }

    onRowKeydown(event, row) {
      var idx = this.rowNav.indexOf(row);
      var next = null;
      if (event.key === 'ArrowDown' || event.key === 'Down') {
        next = this.rowNav[Math.min(idx + 1, this.rowNav.length - 1)];
      } else if (event.key === 'ArrowUp' || event.key === 'Up') {
        next = this.rowNav[Math.max(idx - 1, 0)];
      } else if ((event.key === 'ArrowRight' || event.key === 'Right') && row.hasAttribute('data-glossee-hamburger-row')) {
        event.preventDefault();
        this.activateRow(row.getAttribute('data-glossee-hamburger-row'), { focusPanel: true });
        return;
      }
      if (next) { event.preventDefault(); next.focus(); }
    }

    setHamburgerState(open) {
      if (!this.hamburgerSummary) return;
      this.hamburgerSummary.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.header.classList.toggle('glossee-hamburger-open', open);
    }

    close(options) {
      this.cancelOpen();
      this.cancelClose();
      this.cancelRow();
      if (!this.mode) return;
      var self = this;
      var restore = options && options.restoreFocus ? this.lastTrigger : null;
      this.mode = null;
      this.openId = null;
      this.triggers.forEach(function (trigger) { trigger.setAttribute('aria-expanded', 'false'); });
      this.rows.forEach(function (row) { row.setAttribute('aria-expanded', 'false'); row.classList.remove('is-active'); });
      this.setHamburgerState(false);
      this.setShellState(false);
      this.pauseAllVideos();
      // the content stays in place while the shell fades out; the layout is reset afterwards
      this.cancelCleanup();
      this.cleanupTimer = setTimeout(function () {
        self.cleanupTimer = null;
        if (self.mode) return;
        self.panels.forEach(function (panel) {
          if (panel.glosseeHideTimer) { clearTimeout(panel.glosseeHideTimer); panel.glosseeHideTimer = null; }
          panel.classList.remove('is-active');
          panel.hidden = true;
        });
        if (self.aside) self.aside.hidden = true;
        if (self.asidePromos) self.asidePromos.hidden = true;
        self.classList.remove('glossee-mega--hamburger', 'glossee-mega--fallback-promos');
      }, reducedMotion() ? 0 : SHELL_MS + 20);
      if (restore && typeof restore.focus === 'function') {
        // returning focus to a top menu item must not reopen its panel through the focusin handler
        this.restoringFocus = true;
        restore.focus();
        this.restoringFocus = false;
      }
    }

    /* ------------------------------------------------------------------ media cards */
    /* only videos inside the visible panel (or the visible default cards) play; everything else is paused */
    syncVideos() {
      var videos = this.querySelectorAll('video.glossee-mega__promo-video');
      if (!videos.length) return;
      var roots = [];
      if (this.mode && this.isDesktop()) {
        var panel = this.openId ? this.panelFor(this.openId) : null;
        if (panel) roots.push(panel);
        if (this.mode === 'hamburger' && this.asidePromos && !this.asidePromos.hidden) roots.push(this.asidePromos);
      }
      var allow = !reducedMotion();
      Array.prototype.forEach.call(videos, function (video) {
        var visible = allow && video.getClientRects().length > 0 && roots.some(function (root) { return root.contains(video); });
        if (visible) {
          video.muted = true;
          video.defaultMuted = true;
          video.playsInline = true;
          video.loop = true;
          if (video.preload === 'none') video.preload = 'auto';
          if (video.paused) {
            var playing = video.play();
            if (playing && typeof playing.catch === 'function') playing.catch(function () {});
          }
        } else if (!video.paused) {
          video.pause();
        }
      });
    }

    pauseAllVideos() {
      Array.prototype.forEach.call(this.querySelectorAll('video.glossee-mega__promo-video'), function (video) {
        if (!video.paused) video.pause();
      });
    }
  }

  customElements.define('glossee-mega-menu', GlosseeMegaMenu);

  /* ---- Mobile drawer, blocks source (Phase 3.4): rows and column groups open in place with the reference's
     500ms height animation (cubic-bezier(.4,0,.2,1)); one delegated listener, instance-safe, works after
     Theme Editor section reloads. The theme's MenuDrawer (open / close / focus trap / Escape) is untouched. */
  function toggleMobilePanel(button) {
    var id = button.getAttribute('aria-controls');
    var panel = id ? document.getElementById(id) : null;
    if (!panel) return;
    var open = button.getAttribute('aria-expanded') === 'true';
    var motion = !reducedMotion() && typeof panel.animate === 'function';
    panel.getAnimations().forEach(function (a) { a.cancel(); });
    if (open) {
      button.setAttribute('aria-expanded', 'false');
      if (!motion) { panel.hidden = true; return; }
      var h = panel.offsetHeight;
      var closing = panel.animate([{ height: h + 'px' }, { height: '0px' }], { duration: MOBILE_ACCORDION_MS, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' });
      var finishClose = function () { if (button.getAttribute('aria-expanded') === 'false') panel.hidden = true; };
      closing.onfinish = finishClose;
      setTimeout(finishClose, MOBILE_ACCORDION_MS + 50); // safety: a paused background tab never leaves the panel half-closed
    } else {
      button.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      if (!motion) return;
      var target = panel.scrollHeight;
      panel.animate([{ height: '0px' }, { height: target + 'px' }], { duration: MOBILE_ACCORDION_MS, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' });
    }
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest ? event.target.closest('[data-glossee-mdrawer-toggle]') : null;
    if (!button) return;
    event.preventDefault();
    toggleMobilePanel(button);
  });

  /* ---- Editorial desktop header: the sticky header stays visible while scrolling.
     Reuses the theme's StickyHeader element (header.liquid). On desktop, for the editorial layout only,
     its hide() is redirected to "stick to the top but stay visible", and the header becomes sticky as soon
     as it reaches the viewport top (the theme would otherwise wait until it scrolled fully out of view and
     hide it on scroll down). Since Phase 3.4.1 this applies at every width (reference: sticky on mobile too);
     the other header layouts keep the theme behaviour. */
  function installEditorialSticky() {
    var wrapper = document.querySelector('.shopify-section-header > .header-wrapper--editorial');
    if (!wrapper || wrapper.tagName.toLowerCase() !== 'sticky-header' || wrapper.__glosseeStickyInstalled) return;
    if (typeof wrapper.hide !== 'function') return;
    var section = wrapper.closest('.shopify-section-header');
    var mq = window.matchMedia(DESKTOP);
    var originalHide = wrapper.hide;
    wrapper.__glosseeStickyInstalled = true;

    function stickVisible() {
      section.classList.add('shopify-section-header-sticky');
      section.classList.remove('shopify-section-header-hidden');
      document.body.classList.add('shopify-body-section-header-sticky');
      document.body.classList.remove('shopify-body-section-header-hidden');
    }

    // Phase 3.4.1: every width — the reference header stays visible (position: sticky) on phones as well
    wrapper.hide = function () {
      if (this.isAtTop) return;
      stickVisible();
    };

    window.addEventListener('scroll', function () {
      var bounds = wrapper.headerBounds;
      if (!bounds || typeof bounds.top !== 'number') return;
      var y = window.pageYOffset || document.documentElement.scrollTop;
      if (y > 10 && y > bounds.top) {
        if (!section.classList.contains('shopify-section-header-sticky') || section.classList.contains('shopify-section-header-hidden')) {
          stickVisible();
        }
      }
    }, { passive: true });

    // desktop fallback (theme drawer opened from the desktop hamburger): the theme sets aria-expanded="true"
    // on the summary when opening but never resets it; keep the attribute truthful on desktop.
    document.addEventListener('menudrawer:close', function () {
      var summary = section.querySelector('.header--middle-center .header__left header-drawer > details > summary');
      if (summary) summary.setAttribute('aria-expanded', 'false');
    });
  }

  if (customElements.get('sticky-header')) {
    installEditorialSticky();
  } else {
    customElements.whenDefined('sticky-header').then(installEditorialSticky);
  }
})();
